import { Button, IconButton } from '@mui/material';
import HeaderPageBlock from '@ui/Blocks/Shared/HeaderPageBlock';
import {
  QboAdvanceTable,
  QboCard,
  QboCardCaption,
  QboFormGroup,
  QboTextField,
  QboTypography,
  TooltipWrapper,
} from '@ui/Components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import NoStatusSection from '@ui/Sections/SystemStatusPage/NoStatusSection';
import AuditLogDetailsSection from '@ui/Sections/AuditLogsPage/AuditLogDetailsSection';
import NoStatus from '@images/generals/NoStatus.svg';
import ITDateRangePicker from '@ui/Components/ITDateRangePicker';
import ActivitiesFilterSection from '@ui/Sections/AuditLogsPage/ActivitiesFilterSection';
import ActorRolesFilterSection from '@ui/Sections/AuditLogsPage/ActorRolesFilterSection';
import ActorsFilterSection from '@ui/Sections/AuditLogsPage/ActorsFilterSection';
import SourcesFilterSection from '@ui/Sections/AuditLogsPage/SourcesFilterSection';
import ObjectTypesFilterSection from '@ui/Sections/AuditLogsPage/ObjectTypesFilterSection';
import ObjectsFilterSection from '@ui/Sections/AuditLogsPage/ObjectsFilterSection';
import QboSortByButton from '@ui/Components/QboSortByButton';
import PropTypes from 'prop-types';
import PartnerFilterSection from '@ui/Sections/Shared/PartnerFilterSection';
import useAuditLogs from './pageHooks/useAuditLogs';
import useDataService from './pageHooks/useDataService';

export default function AuditLogsPage({ partnerId }) {
  const { t } = useTranslation();

  const {
    filterState,
    handleAdvancedFilterClick,
    handleOnSearchFieldChange,
    handleOnDateRangeChange,
    handleOnActivitiesChange,
    handleOnActorRolesChange,
    handleOnActorsChange,
    handleOnSourcesChange,
    handleOnObjectTypesChange,
    handleOnObjectsChange,
    handleOnStatusChange,
    actualPartnerId,
    handlePartnerIdChange,
    handleOnSearchClick,
    handleOnResetClick,
    handleOnRefreshClick,
    handleCloseModal,
    getAuditLogsState,
    matchText,
    logsData,
    rowsCount,
    pagination,
    setPagination,
    clickableColumnsList,
    selectedItem,
  } = useAuditLogs({ partnerId });

  const { auditLogsHeaders, statusOptions } = useDataService();

  const refreshButton = (
    <IconButton style={{ marginRight: 25 }} onClick={handleOnRefreshClick}>
      <RefreshIcon
        sx={{
          width: '2rem',
          height: '2rem',
        }}
      />
    </IconButton>
  );

  return (
    <>
      {!partnerId && <HeaderPageBlock>{t('audit_logs.title')}</HeaderPageBlock>}
      <QboCard>
        <QboCard isMainWrapper>
          <QboFormGroup
            onSubmit={handleOnSearchClick}
            buttonPostion="start"
            handleOnResetButton={handleOnResetClick}
            firstButtonLabel={t('partner_details_page.button.search')}
            withGenerateAndResetButton
            additionalButtons={refreshButton}>
            <TooltipWrapper
              condition={!actualPartnerId}
              title={t('audit_logs.select_partner_filter')}>
              <QboTextField
                id="payloadSearch"
                value={filterState.search}
                isWidthSetExplicit
                placeholder={t('audit_logs.search_placeholder')}
                inputProps={{ maxLength: 255 }}
                onChange={handleOnSearchFieldChange}
                disabled={!actualPartnerId}
              />
            </TooltipWrapper>
            {!partnerId && (
              <div>
                <PartnerFilterSection
                  label="Partner:"
                  value={actualPartnerId}
                  marginRightValue={2}
                  inputSize={180}
                  handleChange={handlePartnerIdChange}
                />
              </div>
            )}
            <div style={{ width: '25rem', marginTop: '1rem' }}>
              <TooltipWrapper
                style={{ width: '100%' }}
                condition={!actualPartnerId}
                title={t('audit_logs.select_partner_filter')}>
                <ITDateRangePicker
                  value={filterState.dateRange}
                  onChange={handleOnDateRangeChange}
                  label="Date Range"
                  disabled={!actualPartnerId}
                />
              </TooltipWrapper>
            </div>
            <div style={{ marginTop: 8 }}>
              <TooltipWrapper
                style={{ width: 'max-content' }}
                condition={!actualPartnerId}
                title={t('audit_logs.select_partner_filter')}>
                <Button
                  disableRipple
                  disabled={!actualPartnerId}
                  style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    textTransform: 'none',
                  }}
                  onClick={handleAdvancedFilterClick}>
                  {filterState.showAdvancedFilter
                    ? t('audit_logs.button.hide_advance_search')
                    : t('audit_logs.button.show_advance_search')}
                </Button>
              </TooltipWrapper>
            </div>
            {filterState.showAdvancedFilter && (
              <div className="AuditLogs__filter-section">
                <ActivitiesFilterSection
                  partnerId={actualPartnerId}
                  value={filterState.activities}
                  onChange={handleOnActivitiesChange}
                />
                <SourcesFilterSection
                  partnerId={actualPartnerId}
                  value={filterState.sources}
                  onChange={handleOnSourcesChange}
                />
                <ActorRolesFilterSection
                  partnerId={actualPartnerId}
                  value={filterState.actorRoles}
                  onChange={handleOnActorRolesChange}
                />
                <ActorsFilterSection
                  partnerId={actualPartnerId}
                  value={filterState.actors}
                  onChange={handleOnActorsChange}
                />
                <ObjectTypesFilterSection
                  partnerId={actualPartnerId}
                  value={filterState.objectTypes}
                  onChange={handleOnObjectTypesChange}
                />
                <ObjectsFilterSection
                  partnerId={actualPartnerId}
                  value={filterState.objects}
                  onChange={handleOnObjectsChange}
                />
                <QboSortByButton
                  defaultValue="All"
                  value={filterState.status}
                  buttonWidth={220}
                  startAdornmentTextWidth={140}
                  startAdornmentText="Status:"
                  handleChange={handleOnStatusChange}
                  menuItemList={statusOptions}
                />
              </div>
            )}
          </QboFormGroup>
        </QboCard>
        {actualPartnerId ? (
          <>
            {matchText && (
              <QboTypography
                sx={{
                  marginLeft: 2,
                  marginTop: 2,
                  marginBottom: 3,
                  fontSize: '15px !important',
                  color: '#42526e',
                  fontWeight: 600,
                  textWrap: 'wrap',
                }}>
                {matchText}
              </QboTypography>
            )}
            {(logsData.length || getAuditLogsState.isLoading) && (
              <QboAdvanceTable
                enableColumnClickable
                columnsClickableList={clickableColumnsList}
                isLoading={getAuditLogsState.isLoading}
                enableTopToolBar={false}
                headers={auditLogsHeaders}
                rows={logsData}
                pagination={pagination}
                setPagination={setPagination}
                rowsCount={rowsCount}
              />
            )}
            {!getAuditLogsState.data?.logs?.length && !getAuditLogsState.isLoading && (
              <NoStatusSection />
            )}
          </>
        ) : (
          <QboCard noPadding isMainWrapper>
            <QboCardCaption imageUrl={NoStatus} heading={t('audit_logs.select_partner')} />
          </QboCard>
        )}
      </QboCard>
      <AuditLogDetailsSection auditLog={selectedItem} onClose={handleCloseModal} />
    </>
  );
}

AuditLogsPage.propTypes = {
  partnerId: PropTypes.number,
};

AuditLogsPage.defaultProps = {
  partnerId: null,
};
